import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { settingsApi } from '../../services/api';

const Footer: React.FC = () => {
  const { language, t } = useLanguageContext();
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    settingsApi.getWebsiteSettings()
      .then(res => {
        if (res.success && res.data) {
          setSiteSettings(res.data);
        }
      })
      .catch(() => {
        // 使用默认设置
      });
  }, []);

  const contactAddress = language === 'zh'
    ? siteSettings.company_address_zh || '越南北宁省'
    : siteSettings.company_address_vi || 'Bắc Ninh, Việt Nam';

  const footerLinks = {
    company: {
      title: language === 'zh' ? '公司信息' : 'Thông tin công ty',
      links: [
        { path: '/about', label: t('about') },
        { path: '/services', label: t('services') },
        { path: '/projects', label: t('projects') },
        { path: '/contact', label: t('contact') },
      ]
    },
    services: {
      title: language === 'zh' ? '业务范围' : 'Dịch vụ',
      links: [
        { label: language === 'zh' ? '电气工程' : 'Công trình điện' },
        { label: language === 'zh' ? '暖通空调' : 'Hệ thống HVAC' },
        { label: language === 'zh' ? '通风系统' : 'Hệ thống thông gió' },
        { label: language === 'zh' ? '建筑装饰' : 'Trang trí công trình' },
        { label: language === 'zh' ? '改造工程' : 'Công trình cải tạo' },
        { label: language === 'zh' ? '防水地坪漆' : 'Sơn sàn chống thấm' },
      ]
    },
    contact: {
      title: language === 'zh' ? '联系我们' : 'Liên hệ',
      info: [
        { 
          icon: '📍',
          text: contactAddress
        },
        { 
          icon: '📞',
          text: siteSettings.contact_phone || '+84 987 654 321'
        },
        { 
          icon: '✉️',
          text: siteSettings.contact_email || 'info@huiya.com.vn'
        },
      ]
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">H</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  {t('companyName')}
                </h3>
                <p className="text-gray-400 mt-1">{t('slogan')}</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {language === 'zh'
                ? '惠亚建筑工程有限公司专注工业厂房及外资、中资工厂建筑工程，提供电气、暖通空调、通风、建筑装饰改造、防水地坪漆等一体化工程服务。立足越南北宁，服务当地工业厂房，拥有丰富的本地化施工经验。'
                : 'Công ty TNHH Xây dựng Hueya chuyên xây dựng nhà xưởng công nghiệp và nhà máy FDI, Trung Quốc, cung cấp dịch vụ công trình tích hợp bao gồm điện, HVAC, thông gió, cải tạo trang trí công trình, sơn sàn chống thấm. Đặt trụ sở tại Bắc Ninh, Việt Nam, phục vụ nhà xưởng công nghiệp địa phương, có kinh nghiệm thi công nội địa phong phú.'
              }
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">
              {footerLinks.company.title}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.links.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">
              {footerLinks.services.title}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.links.map((service, index) => (
                <li key={index}>
                  <span className="text-gray-400">
                    {service.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {footerLinks.contact.info.map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-gray-400">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-gray-950 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {t('companyName')}. {t('allRightsReserved')}
            </div>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-500 hover:text-white transition-colors">
                {language === 'zh' ? '隐私政策' : 'Chính sách bảo mật'}
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors">
                {language === 'zh' ? '服务条款' : 'Điều khoản dịch vụ'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;